/**
 * @fileoverview Hook de UI Layer para gestión de handlers de selección en ConversationTopicsModalMC.
 * Permite exponer handlers memoizados para selección de tópico y sub-tópico, siguiendo separación estricta de responsabilidades.
 * @module useConversationTopicsModalUI
 * @category Hook - UI Layer
 * @since 3.1.1
 * @version 3.1.1
 */
/**
 * @typedef {Object} UseConversationTopicsModalUIParams
 * @description Parámetros requeridos para el hook UI de selección de tópicos.
 * @property {function} setSelectedTopic - Setter de tópico
 * @property {function} setSelectedSubtopic - Setter de sub-tópico
 */

/**
 * @typedef {Object} UseConversationTopicsModalUIReturn
 * @description Objeto retornado por el hook useConversationTopicsModalUI.
 * @property {function} handleSelectTopic - Handler para seleccionar tópico y resetear sub-tópico
 * @property {function} handleSelectSubtopic - Handler para seleccionar sub-tópico
 */

/**
 * Hook UI Layer para exponer handlers de selección de tópico y sub-tópico en ConversationTopicsModalMC.
 *
 * Implementa el patrón de **UI Layer** con separación estricta de responsabilidades:
 * - Solo lógica visual y handlers de UI
 * - Sin lógica de datos ni operaciones de negocio
 * - Memoización para performance
 *
 * **Arquitectura Optimizada (v3.1.1):**
 * - ✅ Solo handlers de UI (selección de tópico/sub-tópico)
 * - ✅ useCallback para performance
 *
 * @function useConversationTopicsModalUI
 * @param {UseConversationTopicsModalUIParams} params - Parámetros requeridos para el hook
 * @returns {UseConversationTopicsModalUIReturn} Handlers memoizados para selección
 *
 * @see {@link ../../../../modules/inbox/components/ConversationTopicsModalMC.js} Uso en modal de tópicos
 *
 * @since 3.1.1
 * @version 3.1.1 - Documentación y typedefs agregados
 */
import { useCallback } from 'react';

/**
 * Hook UI Layer para exponer handlers de selección de tópico y sub-tópico.
 *
 * @param {Object} params
 * @param {function} params.setSelectedTopic - Setter de tópico
 * @param {function} params.setSelectedSubtopic - Setter de sub-tópico
 * @returns {Object} Handlers memoizados para selección
 */
export default function useConversationTopicsModalUI({ setSelectedTopic, setSelectedSubtopic }) {
  /**
   * Handler para seleccionar tópico y resetear sub-tópico.
   *
   * @function handleSelectTopic
   * @memberof useConversationTopicsModalUI
   * @param {string|number} value - ID del tópico seleccionado
   * @returns {void}
   *
   * @example
   * handleSelectTopic(1);
   */
  const handleSelectTopic = useCallback((value) => {
    setSelectedTopic(value);
    setSelectedSubtopic(null);
  }, [setSelectedTopic, setSelectedSubtopic]);

  /**
   * Handler para seleccionar sub-tópico.
   *
   * @function handleSelectSubtopic
   * @memberof useConversationTopicsModalUI
   * @param {string|number} value - ID del sub-tópico seleccionado
   * @returns {void}
   *
   * @example
   * handleSelectSubtopic(5);
   */
  const handleSelectSubtopic = useCallback((value) => {
    setSelectedSubtopic(value);
  }, [setSelectedSubtopic]);

  return {
    handleSelectTopic,
    handleSelectSubtopic
  };
}
